import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Grid,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';

import { Creators as ProductCreators } from 'store/products/products-duck.js';
import { Creators as CategoryCreators } from 'store/categories/categories-duck.js';

import { RichTextEditor } from 'components/rich-text-editor';
import ImageCarousel from 'components/image-carousel';
import ProductTagsEditor from '../tags-editor';
import { resolveImageUrl } from 'utils/apiOrigin';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CollectionsIcon from '@mui/icons-material/Collections';

const ACCEPT = 'image/jpeg,image/png,image/webp';

const ProductModal = ({
  isOpen,
  productToUpdate,
  productCloseModal,
  productCreateAsync,
  productUpdateAsync,
  categories,
  categoriesFetchAsync,
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [enablePromotionalPrice, setEnablePromotionalPrice] = useState(false);

  // Imagens atuais (URLs) visíveis no produto (podem ser removidas uma a uma)
  const [existingImages, setExistingImages] = useState([]);
  // Novos arquivos adicionados nesta sessão
  const [newFiles, setNewFiles] = useState([]);

  const singleFileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);

  const methods = useForm();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = methods;

  // Prévia combinada: imagens que permanecerão (existingImages) + novas (newFiles)
  const previewImages = useMemo(() => {
    const urls = (existingImages || []).map((u) => resolveImageUrl(u));
    const files = newFiles.map((f) => URL.createObjectURL(f));
    return [...urls, ...files];
  }, [existingImages, newFiles]);

  // Revoga blobs ao fechar/trocar
  useEffect(() => {
    return () => {
      newFiles.forEach((f) => URL.revokeObjectURL(f.preview || ''));
    };
  }, [newFiles]);

  // Carregamento inicial ao abrir
  useEffect(() => {
    if (isOpen) {
      reset(productToUpdate);
      categoriesFetchAsync();

      // Prepara estado de imagens
      const current = productToUpdate?.images || [];
      setExistingImages(current);
      setNewFiles([]);

      setEnablePromotionalPrice(!!productToUpdate?.promotionalPrice);

      if (productToUpdate?.categoryId) {
        setSelectedCategory(productToUpdate.categoryId);
        setValue('categoryId', productToUpdate.categoryId);
        setSelectedSubCategory(productToUpdate.subCategoryId || '');
        setValue('subCategoryId', productToUpdate.subCategoryId || '');
      } else {
        setSelectedCategory(null);
        setSelectedSubCategory(null);
      }
    } else {
      reset();
      setExistingImages([]);
      setNewFiles([]);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setEnablePromotionalPrice(false);
    }
  }, [isOpen, productToUpdate, reset, categoriesFetchAsync, setValue]);

  // Ações de imagens
  const handleAddSingleClick = () => singleFileInputRef.current?.click();
  const handleAddBatchClick = () => multiFileInputRef.current?.click();

  const onPickSingle = (e) => {
    const file = e.target.files?.[0];
    if (file) setNewFiles((prev) => [...prev, file]);
    e.target.value = ''; // permite escolher o mesmo arquivo novamente, se quiser
  };

  const onPickBatch = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setNewFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeExistingAt = (idx) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeNewAt = (idx) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = async (data) => {
    // Monta payload completo
    const payload = {
      name: data.name,
      price: data.price,
      promotionalPrice: data.promotionalPrice ?? null,
      description: data.description,
      technicalSpecifications: data.technicalSpecifications,
      active: !!data.active,
      highlighted: !!data.highlighted,
      categoryId: data.categoryId || null,
      subCategoryId: data.subCategoryId || null,

      // ⚠️ Importante: enviar o conjunto que deve permanecer + os novos arquivos
      images: existingImages, // URLs que sobraram (se removeu, não vão)
      imagesFiles: newFiles, // novos arquivos (podem ser 0..N)
    };

    if (productToUpdate?.id) {
      productUpdateAsync(productToUpdate.id, payload);
    } else {
      productCreateAsync(payload);
    }

    handleClose();
  };

  const handleClose = () => {
    reset();
    setExistingImages([]);
    setNewFiles([]);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setEnablePromotionalPrice(false);
    productCloseModal();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: { height: '80vh', display: 'flex', flexDirection: 'column' } } }}
    >
      <DialogTitle>{productToUpdate?.id ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          <Grid container>
            {/* ----- Imagens ----- */}
            <Grid size={12} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Imagens do Produto
              </Typography>
              {/* Preview grande (mix de atuais + novas) */}
              {previewImages.length > 0 && (
                <Box mt={1}>
                  <ImageCarousel images={previewImages} />
                </Box>
              )}

              {/* Controles de adição */}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <input
                  type="file"
                  accept={ACCEPT}
                  ref={singleFileInputRef}
                  onChange={onPickSingle}
                  style={{ display: 'none' }}
                />
                <input
                  type="file"
                  accept={ACCEPT}
                  multiple
                  ref={multiFileInputRef}
                  onChange={onPickBatch}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddPhotoAlternateIcon />}
                  onClick={handleAddSingleClick}
                >
                  Adicionar imagem
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CollectionsIcon />}
                  onClick={handleAddBatchClick}
                >
                  Adicionar em lote
                </Button>
              </Stack>

              {/* Listas (atuais e novas) */}
              <Grid container sx={{ mt: 2 }} spacing={2}>
                {/* Imagens atuais (somente em edição) */}
                {productToUpdate?.id && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Imagens atuais
                    </Typography>
                    {existingImages.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma imagem atual.
                      </Typography>
                    )}
                    <Stack spacing={1}>
                      {existingImages.map((url, idx) => (
                        <Box
                          key={`existing-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <img
                            src={resolveImageUrl(url)}
                            alt=""
                            width={56}
                            height={56}
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                          />
                          <Typography variant="body2" noWrap sx={{ flex: 1 }} title={url}>
                            {url}
                          </Typography>
                          <Tooltip title="Remover esta imagem do produto">
                            <IconButton color="error" onClick={() => removeExistingAt(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                )}

                {/* Novas imagens (desta sessão) */}
                <Grid size={{ xs: 12, md: productToUpdate?.id ? 6 : 12 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Novas imagens
                  </Typography>
                  {newFiles.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Nenhuma nova imagem selecionada.
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {newFiles.map((file, idx) => (
                      <Box
                        key={`new-${idx}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          width={56}
                          height={56}
                          style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                        <Typography variant="body2" noWrap sx={{ flex: 1 }} title={file.name}>
                          {file.name}
                        </Typography>
                        <Tooltip title="Remover arquivo da lista">
                          <IconButton color="error" onClick={() => removeNewAt(idx)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* ----- Dados principais ----- */}
            <TextField
              label="Nome"
              variant="filled"
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name?.message}
              {...register('name', { required: 'O nome é obrigatório' })}
            />

            <Controller
              name="price"
              control={methods.control}
              rules={{ required: 'O preço é obrigatório' }}
              render={({ field: { onChange, value } }) => (
                <NumericFormat
                  customInput={TextField}
                  label="Preço"
                  variant="filled"
                  fullWidth
                  margin="normal"
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="R$ "
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale
                  value={value ?? ''}
                  onValueChange={(vals) => onChange(vals.floatValue ?? 0)}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={enablePromotionalPrice}
                  onChange={(e) => {
                    setEnablePromotionalPrice(e.target.checked);
                    if (!e.target.checked) setValue('promotionalPrice', null);
                  }}
                />
              }
              label="Habilitar Preço Promocional"
            />

            {enablePromotionalPrice && (
              <Controller
                name="promotionalPrice"
                control={methods.control}
                render={({ field: { onChange, value } }) => (
                  <NumericFormat
                    customInput={TextField}
                    label="Preço Promocional"
                    variant="filled"
                    fullWidth
                    margin="normal"
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="R$ "
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    value={value ?? ''}
                    onValueChange={(vals) => onChange(vals.floatValue ?? 0)}
                  />
                )}
              />
            )}

            {/* ----- Categoria / Subcategoria ----- */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="category-label" shrink>
                Categoria
              </InputLabel>
              <Select
                notched
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setValue('categoryId', e.target.value);
                  setValue('subCategoryId', '');
                  setSelectedSubCategory('');
                }}
                labelId="category-label"
                label="Categoria"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              <input type="hidden" {...register('categoryId')} />
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={!selectedCategory}>
              <InputLabel id="sub-category-label" shrink>
                Subcategoria
              </InputLabel>
              <Select
                notched
                labelId="sub-category-label"
                label="Sub Categoria"
                value={selectedSubCategory}
                onChange={(e) => {
                  setSelectedSubCategory(e.target.value);
                  setValue('subCategoryId', e.target.value);
                }}
                disabled={
                  !selectedCategory ||
                  categories.find((cat) => cat.id === selectedCategory)?.subCategories?.length < 1
                }
              >
                {categories
                  .find((cat) => cat.id === selectedCategory)
                  ?.subCategories?.map((sub) => (
                    <MenuItem key={sub.id} value={sub.id}>
                      {sub.name}
                    </MenuItem>
                  ))}
              </Select>
              <input type="hidden" {...register('subCategoryId')} />
            </FormControl>

            {/* ----- Flags ----- */}
            <Controller
              name="active"
              control={methods.control}
              defaultValue={productToUpdate?.active ?? true}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />
                  }
                  label="Ativo"
                />
              )}
            />
            <Controller
              name="highlighted"
              control={methods.control}
              defaultValue={productToUpdate?.highlighted ?? false}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />
                  }
                  label="Destaque"
                />
              )}
            />

            {/* ----- Tags ----- */}
            <Grid size={12}>
              <ProductTagsEditor />
            </Grid>

            {/* ----- Descrição / Especificações ----- */}
            <RichTextEditor
              label="Descrição"
              value={getValues('description')}
              onChange={(val) => setValue('description', val)}
              error={errors.description}
            />
            <RichTextEditor
              label="Especificações Técnicas"
              value={getValues('technicalSpecifications')}
              onChange={(val) => setValue('technicalSpecifications', val)}
              error={errors.technicalSpecifications}
            />
          </Grid>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={productCloseModal} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.Product.isModalOpen,
  productToUpdate: state.Product.productToUpdate,
  categories: state.Category.categories,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...ProductCreators, ...CategoryCreators }, dispatch);

ProductModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  productToUpdate: PropTypes.object,
  productCloseModal: PropTypes.func.isRequired,
  productCreateAsync: PropTypes.func.isRequired,
  productUpdateAsync: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
  categoriesFetchAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductModal);
